import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Routes, Route, BrowserRouter as Router, useParams } from 'react-router-dom';
import './App.css';
import {
  Box,
  CssBaseline,
  CircularProgress,
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from '@mui/material';
import lightTheme from './lib/themes/light';
import darkTheme from './lib/themes/dark';
import { ThemeContext, ThemeProvider } from './lib/contexts/ThemeContext';
//import { ADMIN_PAGE_ROUTE, HOME_PAGE_ROUTE, SETTINGS_PAGE_ROUTE, PUBLIC_URL } from './lib/constants/routes';
import { AdminScreen, LoginScreen, NotFoundScreenInternal } from './screens';
import { CenteredContent, Screen, PageLoadError } from './components';
import { UserContext, UserContextProvider } from './lib/contexts/UserContext';
//import CacheBuster from './lib/helpers/cacheBuster';
import { QueryClient, QueryClientProvider } from 'react-query';
import { DashboardScreen } from './screens/DashboardScreen';
import { PartnerContext, PartnerContextProvider } from './lib/contexts/PartnerContext';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { PatientsScreen } from './screens/PatientsScreen';
import { AdminProgressBar } from './components/AdminProgressBar';
import { BillingScreen } from './screens/BillingScreen';
import { BillingContext, ConditionalBillingContextProvider } from './lib/contexts/BillingContext';
import SetupPlanDialog from './screens/BillingScreen/components/SetupPlanDialog';
import { getBetaClientHost, getClientHost } from './lib/constants/constants';
import { OrderManagementScreen } from './screens/OrderManagementScreen';
import { APIDocScreen } from './screens/APIDocScreen';
import { TestScreen } from './screens/TestScreen';
import OrganizationOnboardingScreen from './screens/OrganizationOnboardingScreen/OrganizationOnboardingScreen';
import LearningCenterScreen from './screens/LearningCenterScreen/LearningCenterScreen';
import ShareLinkDialog from './components/ShareLinkDialog/ShareLinkDialog';
import { ModeContext, ModeContextProvider } from './lib/contexts/ModeContext';
import { Mode } from '@mui/icons-material';
import { PrintProvider } from './lib/contexts/PrintContext';
import { LocaleProvider } from './lib/contexts/LocaleContext';
import DeniedAccessScreen from './screens/NotFoundScreen/DeniedAccessScreen';
import { ConditionalOrdersProvider } from './lib/contexts/OrdersContext';
import { BenefitDetailsContextProvider } from './lib/contexts/BenefitDetailsContext';
import MetricDashboardScreen from './screens/MetricDashboardScreen';
// import SettingsScreen from './screens/SettingsScreen/SettingsScreen';
import UserSettingsScreen from './screens/SettingsScreen/UserSettingsScreen';
import PartnerSettingsScreen from './screens/SettingsScreen/PartnerSettingsScreen';
import OrderDetailsScreen from './screens/OrderManagementScreen/OrderDetailsScreen';
import OrderDetailsFullDisplay from './screens/OrderManagementScreen/components/OrderDetailsFullDisplay';
import OrdersDetailsScreen from './screens/OrderManagementScreen/OrdersDetailsScreen';
import AccountManagementScreen from './screens/OrderManagementScreen/AccountManagementScreen';
import SecretTestPage from './screens/SecretTestPage';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import { SnackbarProvider } from 'notistack';
import { useAnalytics } from './lib/hooks/useAnalytics';
import useCurrentUser from './lib/hooks/useCurrentUser';
import useSession from './lib/hooks/useSession';
import LoginScreenUniversal from './screens/LoginScreen/LoginScreenUniversal';
import CookieConsent from './components/CookieConsent';
import DietAssessmentScreen from './screens/DietAssessmentScreen/DietAssessmentScreen';
import { DietQualityScreen } from './screens/DietQualityScreen';
import CompletionScreen from './components/DietAssessment/components/CompletionScreen';

const App = () => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPlanRequiredDialog, setShowPlanRequiredDialog] = useState(false);
  const [pageAccess, setPageAccess] = useState(['login', 'dashboard', 'patients']);
  const pageSlug = useMemo(
    () => window.location.href.substring(window.location.href.lastIndexOf('/') + 1),
    [window.location],
  );
  const urlParams = new URLSearchParams(window.location.href);
  const resetToken = urlParams.get('reset_token');

  const {
    state: { user_id },
    actions: { setUser, logout },
  } = useContext(UserContext);
  //console.log('userObject', userState)
  const {
    state: { code: partnerCode, userRole },
  } = useContext(PartnerContext);
  //const { userID, partnerUserId } = useCurrentUser();
  useAnalytics({ userRole, partnerCode });

  const {
    state: { subscriptions },
  } = useContext(BillingContext);

  const {
    state: { adminView },
  } = useContext(ModeContext);

  useEffect(() => {
    if (subscriptions && subscriptions.subscription_limit) {
      setShowPlanRequiredDialog(true);
    }
  }, [subscriptions, pageSlug]);

  useEffect(() => {
    if (userRole === 'patient') {
      // window.location.href = getClientHost(partnerCode)+'/assessment';
    }
    if (userRole === '' && resetToken === undefined && partnerCode && pageSlug && pageSlug !== 'docs') {
      logout();
    }
  }, [userRole, partnerCode, resetToken]);

  useEffect(() => {
    setError(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (window.Weglot) {
      window.Weglot.on('languageChanged', (newLang) => {
        // console.log('Locale changed to:', newLang);
      });
    }
  }, []);

  const switchComponent = useCallback((slug) => {
    switch (slug) {
      case 'office_admin':
        return <AdminScreen />;
      case 'settings':
        return <></>; //<SettingsScreen />;
      case 'dashboard':
        return <DashboardScreen />;
      case 'homepage':
      case '/':
      default:
        if (partnerCode) {
          if (partnerCode || partnerCode.role === 'staff') {
            return <AdminScreen />;
          } else {
            return <DashboardScreen />;
          }
        }
    }
  }, []);

  const renderRoute = useCallback(() => {
    //const { slug } = useParams(); // - not working
    const slug = window.location.href.substring(window.location.href.lastIndexOf('/') + 1);
    if (!user_id) {
      return <LoginScreen />;
    } else {
      return partnerCode && pageAccess.includes(slug) ? <>{switchComponent(slug)}</> : <NotFoundScreenInternal />;
    }
  }, [partnerCode, user_id, pageAccess, useParams]);

  if (loading || error) {
    return (
      <Router>
        <Screen>
          <CenteredContent>
            {loading ? <CircularProgress color={'primary'} /> : <PageLoadError message={'Page Load Error'} />}
          </CenteredContent>
        </Screen>
      </Router>
    );
  }

  const wrappedDashboardScreen = (
    <React.Suspense fallback={<AdminProgressBar />}>
      <DashboardScreen />
    </React.Suspense>
  );
  const wrappedPatientsScreen = (
    <React.Suspense fallback={<AdminProgressBar />}>
      <PatientsScreen />
    </React.Suspense>
  );
  const wrappedBillingScreen = (
    <React.Suspense fallback={<AdminProgressBar />}>
      <BillingScreen />
    </React.Suspense>
  );
  const wrappedAPIDocScreen = (
    <React.Suspense fallback={<AdminProgressBar />}>
      <APIDocScreen />
    </React.Suspense>
  );
  const wrappedOrderManagementScreen = (
    <React.Suspense fallback={<AdminProgressBar />}>
      <OrderManagementScreen />
    </React.Suspense>
  );
  const wrappedAccountManagementScreen = (
    <React.Suspense fallback={<AdminProgressBar />}>
      <AccountManagementScreen />
    </React.Suspense>
  );
  const wrappedDietAssessmentScreen = (
    <React.Suspense fallback={<CenteredLoader />}>
      <DietAssessmentScreen />
    </React.Suspense>
  );
  const wrappedDietQualityScreen = (
    <React.Suspense fallback={<AdminProgressBar />}>
      <DietQualityScreen />
    </React.Suspense>
  );
  const wrappedCompletionScreen = (
    <React.Suspense fallback={<AdminProgressBar />}>
      <CompletionScreen />
    </React.Suspense>
  );
  return (
    <>
      <CookieConsent />
      <Router basename={'/'}>
        <Routes>
          <Route
            path="/auth"
            element={
              <div style={{ margin: 'auto', marginTop: '40%', textAlign: 'center' }}>
                {' '}
                <CircularProgress />{' '}
              </div>
            }
          />
          {user_id ? (
            <>
              <Route
                path="/"
                element={adminView ? wrappedDashboardScreen : wrappedOrderManagementScreen}
              />
              <Route
                path="/login"
                element={adminView ? wrappedDashboardScreen : wrappedOrderManagementScreen}
              />
              <Route
                path="/dashboard"
                element={adminView ? wrappedDashboardScreen : wrappedOrderManagementScreen}
              />
              <Route
                path="/assessment"
                element={wrappedDietAssessmentScreen}
              />
              <Route
                path="/assessment/start"
                element={wrappedDietAssessmentScreen}
              />
              <Route
                path="/assessment/resume"
                element={wrappedDietAssessmentScreen}
              />
              <Route
                path="/assessment/:last_current_step"
                element={wrappedDietAssessmentScreen}
              />
              <Route
                path="/patients"
                element={adminView ? wrappedPatientsScreen : <DeniedAccessScreen />}
              />
              <Route
                path="/patients/list/:listId"
                element={adminView ? wrappedPatientsScreen : <DeniedAccessScreen />}
              />
              <Route
                path="/patients/:uuid"
                element={adminView ? wrappedPatientsScreen : <DeniedAccessScreen />}
              />
              <Route
                path="/billing"
                element={<NotFoundScreenInternal />}
              />
              <Route
                path="/docs"
                element={wrappedAPIDocScreen}
              />
              <Route
                path="/orders"
                element={wrappedOrderManagementScreen}
              />
              <Route
                path="/orders/:orderID"
                element={<OrderDetailsScreen />}
              />
              <Route
                path="/orders/:orderID/details"
                element={<OrderDetailsFullDisplay />}
              />
              <Route
                path="/orders/details"
                element={<OrdersDetailsScreen />}
              />
              <Route
                path="/account"
                // element={adminView ? wrappedAccountManagementScreen : wrappedOrderManagementScreen}
                element={wrappedAccountManagementScreen}
              />
              <Route
                path="/secret"
                element={<SecretTestPage />}
              />
              <Route
                path="/messages"
                element={<NotFoundScreenInternal />}
              />
              <Route
                path="/settings"
                element={adminView ? <PartnerSettingsScreen /> : <UserSettingsScreen />}
              />
              <Route
                path="/status"
                element={<NotFoundScreenInternal />}
              />
              <Route
                path="/help"
                element={<NotFoundScreenInternal />}
              />
              <Route
                path="/terms"
                element={<NotFoundScreenInternal />}
              />
              <Route
                path="/guidelines"
                element={<NotFoundScreenInternal />}
              />

              <Route
                path="/organization/onboarding"
                element={<OrganizationOnboardingScreen />}
              />
              <Route
                path="/metrics"
                element={adminView ? <MetricDashboardScreen /> : <DeniedAccessScreen />}
              />

              <Route
                path="/resources"
                element={<NotFoundScreenInternal />}
              />

              <Route
                path="/resume/:sub_category?/*"
                element={<NotFoundScreenInternal />}
              />
              <Route
                path="/assessment/:category?/:sub_category?/*"
                element={<NotFoundScreenInternal />}
              />
              {
                // /assessment/resume/
                // /assessment/start/
                // /assessment/$step
                // /assessment/
              }
              <Route
                path="/completion"
                element={wrappedCompletionScreen}
              />
              <Route
                path="/kiosk/:category?/:sub_category?/*"
                element={<NotFoundScreenInternal />}
              />
              <Route
                path="/learn/:category?/:slug?/*"
                element={<LearningCenterScreen />}
              />
              <Route
                path="/community/:category?/:sub_category?/*"
                element={<NotFoundScreenInternal />}
              />
              <Route
                path="/inbox/:category?/:sub_category?/*"
                element={<NotFoundScreenInternal />}
              />
              <Route
                path="/faq/:category?/:sub_category?/*"
                element={<NotFoundScreenInternal />}
              />

              <Route
                path="/test"
                element={<TestScreen />}
              />
              <Route
                path="/quality"
                element={wrappedDietQualityScreen}
              />
            </>
          ) : (
            <>
              <Route
                path="/docs"
                element={wrappedAPIDocScreen}
                onHoverExpand={false}
              />
              <Route
                path="/universal"
                element={<LoginScreenUniversal />}
              />
              <Route
                path="*"
                element={<LoginScreen />}
              />
              <Route
                path="/organization/onboarding"
                element={<OrganizationOnboardingScreen />}
              />
            </>
          )}
          {/*
        <Route path=":slug" element={renderRoute()} />
        <Route path=":slug/:subSlug" element={renderRoute()} />

        <Route exact path="/:slug?/:sub_slug?" element={renderRoute} />*/}
        </Routes>
      </Router>
    </>
  );
};

export const ThemeContainer = () => {
  const { boot } = useIntercom();
  const {
    state: { finalTheme },
  } = useContext(ThemeContext);
  const {
    state: { userRole, is_tangelo },
  } = useContext(PartnerContext);
  const { userID, partnerUserId, intercomUserHash, intercomUserId } = useCurrentUser();
  const { data: sessionData } = useSession();
  const phoneNumber = useMemo(() => sessionData?.phoneNumber?.replace(/^\+1/, ''), [sessionData]);

  // console.log('here is finalTheme in app.jsx: ', finalTheme);
  // console.log('here is theme: ', theme);
  // console.log('here is primaryColor: ', primaryColor);
  // console.log('here is secondaryColor: ', secondaryColor);
  // console.log('here is darkMode: ', darkMode);
  // console.log('here is finalTheme in app.jsx: ', finalTheme);
  // const {
  //   state: { primary_color, secondary_color },
  // } = useContext(PartnerContext);
  // console.log('here is modeConext ', useContext(ModeContext).state.adminView);
  useEffect(() => {
    if (sessionData && sessionData.intercomUserId && sessionData.intercomUserHash) {
      boot({
        userId: sessionData.intercomUserId,
        userHash: sessionData.intercomUserHash,
      });
    }
  }, [sessionData]);

  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={finalTheme}>
        <CssBaseline />
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </MuiThemeProvider>
    </StyledEngineProvider>
  );
};

export const CenteredLoader = () => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      width="100%"
    >
      <CircularProgress />
    </Box>
  );
};
export const AppContainer = ({ flags, ldClient }) => {
  const queryClient = new QueryClient();
  return (
    <ErrorBoundary>
      <SnackbarProvider>
        <QueryClientProvider client={queryClient}>
          <PartnerContextProvider>
            <UserContextProvider>
              <IntercomProvider
                appId={'ibojj8jg'}
                autoBoot={false}
              >
                <ModeContextProvider>
                  <BenefitDetailsContextProvider>
                    <ConditionalOrdersProvider>
                      <ConditionalBillingContextProvider>
                        <ThemeProvider>
                          <PrintProvider>
                            <LocaleProvider>
                              <ThemeContainer />
                            </LocaleProvider>
                          </PrintProvider>
                        </ThemeProvider>
                      </ConditionalBillingContextProvider>
                    </ConditionalOrdersProvider>
                  </BenefitDetailsContextProvider>
                </ModeContextProvider>
              </IntercomProvider>
            </UserContextProvider>
          </PartnerContextProvider>
        </QueryClientProvider>
      </SnackbarProvider>
    </ErrorBoundary>
  );
};

export default AppContainer;
